import { AuthEventTypeV4 } from '../../common/analytics';
import { ConflictedAuthenticationResult } from '../../generated/api/conflictedAuthenticationResult';
import { DialogType } from '../../common/enums';
import { clearOnboardingId } from '../../selfservice/epics/epicUtils';
import { convertToCommonErrors } from '../../common/utils/errorUtils';
import {
  customerServiceNumberLabelMsg,
  customerServiceNumberOpenLongMsg,
  customerServiceNumberPriceMsg,
  notAbleToProcessContactRequestMsg,
  pleaseContactCustomerServiceMsg,
  t,
} from '../../common/i18n';
import { getConflictingErrors } from '../../selfservice/epics/authenticatedUserEpic';
import { isInBrowser } from '../../common/utils/ssrUtils';
import { showAuthFailedDialog, showDialog } from '../../selfservice/actions';
import type { AuthEventV4 } from '../../common/analytics';
import type { AuthenticationResult } from '../../generated/api/authenticationResult';
import type { CommonError } from '../../common/types/errors';
import type { ShowAuthFailedDialogAction, ShowDialogAction } from '../../selfservice/actions';
import type { SwitchAccountJourneyPoint } from '../../common/enums';
import type { User } from './authState';

export const reduceAuthenticationResult = (user: User, authenticationResult: AuthenticationResult) => {
  user.authenticated = {
    ...(user.authenticated || {}),
    analyticsConsent: authenticationResult.contact?.person?.analyticsConsent,
    businessId: authenticationResult.account?.businessId,
    chatToken: authenticationResult.token,
    companyName: authenticationResult.account?.accountName || '',
    contact: authenticationResult.contact,
    costCenter: authenticationResult.contact?.person?.costCenter,
    email: authenticationResult.contact?.person?.email,
    employeeNumber: authenticationResult.contact?.person?.employeeNumber,
    firstName: authenticationResult.contact?.person?.firstName || '',
    lastName: authenticationResult.contact?.person?.lastName,
    mdmId: authenticationResult.account?.accountMasterId,
    mobile: authenticationResult.contact?.person?.phoneNumber,
    preferredLanguage: authenticationResult.contact?.person?.preferredLanguage || 'fi',
    selfServiceInOec: authenticationResult.account?.selfServiceInOec,
    deviceStoreInOec: authenticationResult.account?.deviceStoreInOec,
    userRightsInSalesforce: authenticationResult.account?.userRightsInSalesforce,
    expiringEppSubscriptions: authenticationResult?.expiringEppSubscriptions,
    secondaryAccounts: authenticationResult.secondaryAccounts,
    enabledFeatureFlags: authenticationResult.enabledFeatureFlags,
    customerLevelPriceOrDiscount: authenticationResult.customerLevelPriceOrDiscount,
    segmentPricingGroup: authenticationResult.segmentPricingGroup,
    saving: false,
    isFetching: false,
    ...authenticationResult.user,
  };
};

export const handleConflictedError = (
  errors?: CommonError[],
  journeyPoint?: SwitchAccountJourneyPoint
): ShowDialogAction | undefined => {
  const conflictingErrors = getConflictingErrors(errors);
  if (conflictingErrors) {
    clearOnboardingId();
    switch (conflictingErrors.type) {
      case ConflictedAuthenticationResult.TypeEnum.CONFLICTING_ACCOUNT:
        return showDialog({
          type: DialogType.SWITCH_ACCOUNT,
          header: `${t.NW12('Confirm pairing subscription and user details to new employer')}${
            conflictingErrors.conflictingAccountName ? ` (${conflictingErrors.conflictingAccountName}).` : `.`
          }`,
          body: t.KJAE(
            'After confirmation you lose access to subscription and usage details from the duration with your previous employer.'
          ),
          journeyPoint: journeyPoint,
        });
      case ConflictedAuthenticationResult.TypeEnum.DUPLICATE_CONTACT:
      case ConflictedAuthenticationResult.TypeEnum.POSSIBLE_CONTACT_DUPLICATE:
        return showDialog({
          type: DialogType.DUPLICATE_CONTACT,
          header: t.AYKR(pleaseContactCustomerServiceMsg),
          body: t.EGFA(notAbleToProcessContactRequestMsg),
        });
      case ConflictedAuthenticationResult.TypeEnum.USER_NOT_FOUND:
        return showDialog({
          type: DialogType.USER_RIGHTS_IN_SALESFORCE,
        });
      case ConflictedAuthenticationResult.TypeEnum.CONFLICTING_CONTACT:
        return showDialog({
          type: DialogType.CONFLICTING_CONTACT,
        });
      default:
        return undefined;
    }
  }
  return undefined;
};

export const handleAuthFailedError = (
  errors?: CommonError[],
  userName?: string,
  password?: string
): ShowAuthFailedDialogAction => {
  if (errors?.length) {
    if (errors[0].source && (errors[0].source.error as unknown as ConflictedAuthenticationResult)) {
      const error = errors[0].source.error as unknown as ConflictedAuthenticationResult;
      if (ConflictedAuthenticationResult.TypeEnum.MULTIPLE_USER_NAMES === error.type && error.userNames) {
        return showAuthFailedDialog({
          type: DialogType.SELECT_USERNAME,
          usernames: error.userNames,
        });
      } else if (
        ConflictedAuthenticationResult.TypeEnum.PRIMARY_ACCOUNT_NOT_SET === error.type &&
        error.availableAccounts &&
        password &&
        userName
      ) {
        return showAuthFailedDialog({
          availableAccounts: error.availableAccounts,
          password: password,
          type: DialogType.SELECT_PRIMARY_COMPANY,
          username: userName,
        });
      } else if (
        ConflictedAuthenticationResult.TypeEnum.PRIMARY_ACCOUNT_NOT_SET === error.type &&
        error.availableAccounts
      ) {
        return showAuthFailedDialog({
          availableAccounts: error.availableAccounts,
          type: DialogType.SELECT_PRIMARY_COMPANY,
        });
      } else if (ConflictedAuthenticationResult.TypeEnum.MOBILE_NUMBER_MISSING_MFA_REQUIRED === error.type) {
        return showAuthFailedDialog({
          type: DialogType.MFA_REQUIRED_PHONE,
        });
      } else if (ConflictedAuthenticationResult.TypeEnum.MOBILE_NUMBER_MISSING === error.type && password && userName) {
        return showAuthFailedDialog({
          password: password,
          type: DialogType.MISSING_MOBILE_NUMBER,
          username: userName,
        });
      }
    } else {
      return showAuthFailedDialog(
        undefined,
        t.JYLX(
          'Something went wrong, please try signing in again. If the problem persists, please call our customer service on {0} ({1}). Our customer service is open {2}.',
          customerServiceNumberLabelMsg,
          t.HQWW(customerServiceNumberPriceMsg),
          t.HQWV(customerServiceNumberOpenLongMsg)
        )
      );
    }
  }
  return showAuthFailedDialog();
};

const redirectToOecFromPaths = ['omaelisa', 'kassa', 'työntekijä'];

export const handleRedirectResponse = (response: Response, forceRedirect = false) => {
  const redirectUrl = response.headers.get('Location');
  if (isInBrowser() && redirectUrl && window.location?.pathname) {
    // note, path always contains slash so this works
    const firstPath = window.location.pathname.split('/')[1];
    if (forceRedirect || redirectToOecFromPaths.some(redirectPath => firstPath.toLowerCase() === redirectPath)) {
      window.location.href = redirectUrl;
    }
  }
};

export const ssoNoeLoginSuccess = (authenticationResult: AuthenticationResult): AuthEventV4 => ({
  event: AuthEventTypeV4.LOGIN,
  login_service: 'ya_noe', // eslint-disable-line @typescript-eslint/naming-convention
  login_method: 'credentials', // eslint-disable-line @typescript-eslint/naming-convention
  sso_user_id: authenticationResult.user.userNameHash, // eslint-disable-line @typescript-eslint/naming-convention
});

export const ssoNoeLoginFail = (errors: CommonError[]): AuthEventV4 => ({
  event: AuthEventTypeV4.LOGIN_FAILED,
  login_service: 'ya_noe', // eslint-disable-line @typescript-eslint/naming-convention
  login_method: 'credentials', // eslint-disable-line @typescript-eslint/naming-convention
  failure_reason: 'user failed to authenticate through sso', // eslint-disable-line @typescript-eslint/naming-convention
  error: 'access denied',
  error_description: errors.map(error => error.message).join(), // eslint-disable-line @typescript-eslint/naming-convention
});

export const elisaIdEoeLoginSuccess = (authenticationResult: AuthenticationResult): AuthEventV4 => ({
  event: AuthEventTypeV4.LOGIN,
  login_service: 'ya_eoe', // eslint-disable-line @typescript-eslint/naming-convention
  login_method: 'credentials', // eslint-disable-line @typescript-eslint/naming-convention
  amr_values: authenticationResult.amr, // eslint-disable-line @typescript-eslint/naming-convention
  eid_user_id: authenticationResult.trackingId, // eslint-disable-line @typescript-eslint/naming-convention
});

export const elisaIdEoeLoginFail = (errors: CommonError[]): AuthEventV4 => ({
  event: AuthEventTypeV4.LOGIN_FAILED,
  login_service: 'ya_eoe', // eslint-disable-line @typescript-eslint/naming-convention
  login_method: 'credentials', // eslint-disable-line @typescript-eslint/naming-convention
  failure_reason: 'user failed to authenticate through elisaIdV2', // eslint-disable-line @typescript-eslint/naming-convention
  error: 'access denied',
  error_description: errors.map(error => error.message).join(), // eslint-disable-line @typescript-eslint/naming-convention
});

// filter errors which are not needed to be set in the state, because they are handled separately, e.g. showing a dialog on HTTP Status CONFLICT (409)
export const filterErrors = (errors?: CommonError[]) => {
  return errors?.filter(
    error =>
      error.source?.statusCode !== 409 ||
      (error.source?.statusCode === 409 &&
        (error.category === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_USED_OR_EXPIRED ||
          error.category === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_OLD_OR_INVALID))
  );
};

export const processConflictedErrors = (errors?: CommonError[]) => {
  return errors?.map(error => {
    if (
      error.source?.statusCode === 409 &&
      error.source?.error &&
      (error.source?.error as unknown as ConflictedAuthenticationResult)
    ) {
      const conflictingError = error.source.error as unknown as ConflictedAuthenticationResult;
      if (
        conflictingError?.type === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_USED_OR_EXPIRED ||
        conflictingError?.type === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_OLD_OR_INVALID
      ) {
        error.category = conflictingError.type;
      }
    }
    return error;
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toCommonErrors = async (error: any) => {
  if (error instanceof Response) {
    let errorBody;
    try {
      errorBody = await error.json();
    } catch (e) {
      errorBody = undefined;
    }
    return convertToCommonErrors(`ajax error ${error.status}`, error.status, errorBody);
  }
  return convertToCommonErrors(error.message);
};
